import React, { useCallback, useEffect, useRef, useState } from "react";
import DepthSnapshot from "./HomeDepthSnapshots";
import HomeFiltersStage from "./HomeFiltersStage";
import HomeTransportStage from "./HomeTransportStage";

function ClipVideo({ src, label, poster }) {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    video.load();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      video.pause();
      return undefined;
    }
    const play = () => video.play().catch(() => {});
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play();
          else video.pause();
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [src]);

  return (
    <video
      key={src}
      ref={ref}
      className="hs-media-clip"
      src={src}
      muted
      loop
      playsInline
      preload="auto"
      poster={poster}
      aria-label={label}
    />
  );
}

const BUCKETS = [
  {
    id: "in",
    title: "In catchment",
    text:
      "You’re inside this school’s published priority area — usually your strongest chance of a place after the exam.",
    samples: [
      { name: "The Tiffin Girls’", meta: "2.4 mi" },
      { name: "Nonsuch High", meta: "3.1 mi" },
    ],
    tone: "green",
  },
  {
    id: "open",
    title: "Open to all",
    text:
      "No home-area gate — anyone can apply. Score and place order decide who gets in, so distance still matters for the school run.",
    samples: [
      { name: "Reading School", meta: "4.2 mi" },
      { name: "Kendrick School", meta: "4.6 mi" },
    ],
    tone: "indigo",
  },
  {
    id: "possible",
    title: "Possible",
    text:
      "Not in a defined catchment — places go by distance, and demand decides how far offers reach.",
    samples: [
      { name: "Wallington Girls", meta: "5.2 mi" },
      { name: "Sutton Grammar", meta: "5.8 mi" },
    ],
    tone: "slate",
  },
  {
    id: "out",
    title: "Outside",
    text:
      "Nearby on the map, but outside the priority area. Still useful to know if you’re shortlisting, or weighing a move into catchment.",
    samples: [
      { name: "Newstead Wood", meta: "7.4 mi" },
      { name: "St Olave’s", meta: "8.1 mi" },
    ],
    tone: "amber",
  },
];

const FEATURE_SPOTLIGHTS = [
  {
    id: "rank",
    kicker: "Ranking",
    title: "See how every grammar stands — then sort by what matters to you",
    lead:
      "One live table for all grammars: GCSE and A level results, Ofsted, leavers destinations, and miles from your home. Sort any column to build a shortlist that fits your priorities — not someone else’s league table.",
    cue: "GCSE · A level · Ofsted · destinations · miles",
    page: "ranking",
    cta: "Open ranking",
    graphic: "rank",
    tone: "teal",
  },
  {
    id: "deadlines",
    kicker: "Exam dates & deadlines",
    title: "See the whole Year 7 calendar — before a date catches you out",
    lead:
      "Grammar entry is a sequence, not a single deadline. Schools also set different residence-by dates for catchment priority — then registration, papers, the CAF, and Offer Day. SchoolCatch lays that path out per school so you can plan around real dates.",
    points: [
      "Know each school’s in-catchment-by date — so a move or stay lines up with priority rules, not just the CAF.",
      "Spot the next action early — register, sit papers, or file the CAF — so busy weeks don’t bury a hard cut-off.",
      "Compare timing across your shortlist: different residence dates, shared tests, and which deadline bites first.",
    ],
    cue: "In catchment by · register · exams · CAF · offer day",
    page: "map",
    cta: "See key dates on the map",
    graphic: "deadlines",
    tone: "slate",
  },
  {
    id: "compare",
    kicker: "Compare",
    title: "Put your shortlist side by side on the same measures",
    lead:
      "Pick a few schools and line them up on one board — gender, Ofsted, distance from home, GCSE, A level, and destinations. Spot the trade-offs clearly before you decide where to apply or where to move.",
    cue: "Overview · GCSE · A level · destinations",
    page: "compare",
    cta: "Open compare",
    graphic: "compare",
    tone: "blue",
  },
];


const DEPTH = [
  {
    id: "panel",
    title: "Exact catchments",
    eyebrow: "Exact catchments",
    blurb: "One school at a time — see each published catchment drawn at the right scale.",
    explain:
      "Browse grammars across London and the South East: each catchment opens alone on the map, fitted to the right zoom, so you see the real priority area — not a rough circle.",
    page: "map",
    cue: "One catchment · fitted zoom · next school",
    graphic: "panel",
    accent: "teal",
  },
  {
    id: "home",
    title: "Set your home",
    eyebrow: "Set your home",
    blurb: "Type a postcode — or drop a pin on the map — so every school is measured from you.",
    explain:
      "Your home pin is the centre of SchoolCatch: catchment standing, distance, transport, and rankings all run from the address or map point you set.",
    page: "map",
    cue: "Postcode · pin on the map",
    graphic: "home",
    accent: "teal",
  },
  {
    id: "alloc",
    title: "Allocation details",
    eyebrow: "Allocation details",
    blurb:
      "See how many sat stage one and stage two, places on offer, and how many sitters there were per place — plus where offers landed.",
    explain:
      "Open a school’s allocation card for recent entry years: how many children sat each exam stage, how many places there were, sitters-per-place (for example about 8× at stage one and about 3× at stage two), and whether offers stayed inside the catchment. Real published patterns — context for how tight it was, never a forecast for your child.",
    page: "map",
    cue: "Stage one · stage two · sitters per place · where offers landed",
    graphic: "alloc",
    accent: "emerald",
  },
  {
    id: "exams",
    title: "Exam details",
    eyebrow: "Exam details",
    blurb:
      "See which subjects this school tests — reasoning, English, maths, and more — plus register dates and whether papers are shared across a county or consortium.",
    explain:
      "Open a school’s exam card for the papers that matter here: subjects (for Kent, verbal, non-verbal, spatial, English, and maths), register-by and sit dates, results timing, and whether one test feeds many grammars. Pulled into one place so you are not hunting the PDF first.",
    page: "map",
    cue: "Subjects · papers · register · shared test",
    graphic: "exams",
    accent: "coral",
  },
  {
    id: "guide",
    title: "School guide",
    eyebrow: "School guide",
    blurb:
      "A calm, school-specific path for parents — what catchment means here, how to apply, exams and place order, then offer day — so you know what to do next, not just what the rules say.",
    explain:
      "Each school guide walks the Year 7 journey in order for that school alone: whether your home is in catchment or open, how to register and name the school on the CAF, what the exams and place order look like here, and what Offer Day means next. It is written for busy parents — the decisions, deadlines, and standing that matter — tied to this school’s published rules, not a generic checklist.",
    page: "map",
    cue: "Catchment → apply → exams → place order → offer day",
    graphic: "guide",
    accent: "gold",
  },
  {
    id: "oxbridge",
    title: "Oxbridge & destinations",
    eyebrow: "Oxbridge & destinations",
    blurb:
      "See school-published Oxbridge offer counts next to DfE leavers destinations — higher education and where sixth-formers went next.",
    explain:
      "Where a school publishes an Oxbridge offer count, it sits beside DfE destinations for that school: higher-education %, further education, and apprenticeships or employment. Useful sixth-form context next to GCSE and A level — never invented, and never a forecast for your child.",
    page: "map",
    cue: "Oxbridge count · higher education · leavers destinations",
    graphic: "oxbridge",
    accent: "blue",
  },
];

function useReveal(selector) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const nodes = [...root.querySelectorAll(selector)];
    nodes.forEach((node) => node.classList.add("hs-wait"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-in");
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [selector]);

  return rootRef;
}

function FeatureSpotlight({ item, onOpenPage }) {
  return (
    <section
      className={`hs-spotlight hs-spotlight--stack hs-reveal hs-spotlight--${item.tone}`}
      aria-label={item.kicker}
    >
      <div className="hs-spotlight-copy">
        <p className="hs-kicker">{item.kicker}</p>
        <h2 className="hs-title hs-title--sm">{item.title}</h2>
        <p className="hs-lead">{item.lead}</p>
        {item.points?.length ? (
          <ul className="hs-spotlight-points">
            {item.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        ) : null}
        <div className="hs-spotlight-actions">
          <span className="hs-spotlight-cue">{item.cue}</span>
          <button type="button" className="hs-spotlight-cta" onClick={() => onOpenPage?.(item.page)}>
            {item.cta}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
      <div className="hs-spotlight-shot is-active">
        <DepthSnapshot kind={item.graphic} />
      </div>
    </section>
  );
}

function DepthExplorer({ onOpenPage }) {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const syncingRef = useRef(false);

  const scrollToCard = useCallback((index, behavior = "smooth") => {
    const track = trackRef.current;
    if (!track) return;
    const next = Math.max(0, Math.min(index, DEPTH.length - 1));
    const card = track.children[next];
    syncingRef.current = true;
    setActive(next);
    if (card) {
      track.scrollTo({ left: card.offsetLeft, behavior });
    }
    window.setTimeout(() => {
      syncingRef.current = false;
    }, 420);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const onScroll = () => {
      if (syncingRef.current) return;
      const cards = [...track.children];
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((card, index) => {
        const center = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      setActive(Math.max(0, Math.min(best, DEPTH.length - 1)));
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    const onResize = () => scrollToCard(active, "auto");
    window.addEventListener("resize", onResize);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [active, scrollToCard]);

  const item = DEPTH[active];

  return (
    <div className="hs-depth-shell hs-depth-shell--stage">
      <div className="hs-depth-main">
        <div className="hs-depth-stage">
          <button
            type="button"
            className="hs-depth-arrow hs-depth-arrow--side hs-depth-arrow--prev"
            aria-label="Previous card"
            disabled={active <= 0}
            onClick={() => scrollToCard(active - 1)}
          >
            ‹
          </button>
          <div className="hs-depth-track" ref={trackRef}>
            {DEPTH.map((entry, index) => (
              <article
                key={entry.id}
                className={`hs-depth-card hs-depth-card--poster${index === active ? " is-active" : ""}`}
              >
                <div className="hs-depth-shot">
                  <DepthSnapshot kind={entry.graphic} />
                </div>
                <div className="hs-depth-card-copy">
                  <p className="hs-kicker hs-kicker--gold">{entry.eyebrow}</p>
                  <h3>{entry.title}</h3>
                  <p>{entry.blurb}</p>
                  <span className="hs-depth-card-cue">{entry.cue}</span>
                </div>
              </article>
            ))}
          </div>
          <button
            type="button"
            className="hs-depth-arrow hs-depth-arrow--side hs-depth-arrow--next"
            aria-label="Next card"
            disabled={active >= DEPTH.length - 1}
            onClick={() => scrollToCard(active + 1)}
          >
            ›
          </button>
        </div>

        <div className="hs-depth-controls">
          <div className="hs-depth-dots" role="tablist" aria-label="Feature cards">
            {DEPTH.map((entry, index) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-label={entry.title}
                aria-selected={index === active}
                className={index === active ? "is-active" : undefined}
                onClick={() => scrollToCard(index)}
              />
            ))}
          </div>
          <p className="hs-depth-swipe" aria-hidden="true">
            <span className="hs-depth-swipe-icon">↔</span>
            <span>Swipe</span>
          </p>
        </div>

        <div className="hs-depth-foot">
          <p className="hs-depth-explain">{item.explain}</p>
          <button type="button" className="hs-depth-cta" onClick={() => onOpenPage?.(item.page)}>
            Open {item.title.toLowerCase()}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HomeStory({ onOpenPage }) {
  const rootRef = useReveal(".hs-reveal");

  return (
    <div className="hs" ref={rootRef}>
      <section className="hs-stage hs-reveal" aria-label="Exact catchments">
        <div className="hs-stage-copy">
          <p className="hs-kicker hs-kicker--teal">Built for parents</p>
          <h2 className="hs-title">
            Meticulous catchments — and the cutting-edge detail that makes decisions easier.
          </h2>
          <p className="hs-lead">
            Every boundary is drawn from the school&apos;s published map, not a rough circle — so you
            can see catchment standing clearly, and where priority areas overlap, before you choose
            a school or a street.
          </p>
          <ul className="hs-stage-points">
            <li>
              Exact catchment drawings show which schools you are in, open to, or outside — so
              shortlists rest on published priority areas, not guesswork.
            </li>
            <li>
              Seeing where catchments overlap helps you weigh trade-offs between neighbouring
              grammars and aim for the strongest chance of a place.
            </li>
            <li>
              Home movers can compare streets and postcodes against real boundaries before they
              commit — so a move is informed by catchment, not hope.
            </li>
          </ul>
          <button type="button" className="hs-link" onClick={() => onOpenPage?.("map")}>
            Open the catchment map
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className="hs-stage-clips hs-stage-clips--single" aria-label="How it looks on the map">
          <figure className="hs-media">
            <ClipVideo
              src="/home-clips/catchment-hero.mp4?v=7"
              poster="/home-clips/catchment-hero.jpg?v=7"
              label="Grammar school catchments stacking and overlapping on the map"
            />
            <figcaption>Stack catchments and see the overlap</figcaption>
          </figure>
        </div>
      </section>

      <section className="hs-band hs-reveal" aria-label="Where you stand">
        <div className="hs-band-head">
          <p className="hs-kicker hs-kicker--emerald">Near your home</p>
          <h2 className="hs-title hs-title--sm">Know the schools near you — and your catchment chances</h2>
          <p className="hs-lead">
            After you set a home, nearby grammars sort into four clear groups — the same standing
            the map uses. See where you are strong, where exams alone decide, and which schools sit
            just out of reach if you stay put or move.
          </p>
        </div>
        <ol className="hs-buckets">
          {BUCKETS.map((bucket, index) => (
            <li key={bucket.id} className={`hs-bucket hs-bucket--${bucket.tone}`}>
              <span className="hs-bucket-index">{String(index + 1).padStart(2, "0")}</span>
              <strong>{bucket.title}</strong>
              <p className="hs-bucket-text">{bucket.text}</p>
              <ul className="hs-bucket-schools" aria-hidden="true">
                {bucket.samples.map((row) => (
                  <li key={row.name}>
                    <span className="hs-bucket-school">{row.name}</span>
                    <span className="hs-bucket-meta">{row.meta}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </section>

      {FEATURE_SPOTLIGHTS.map((item) => (
        <FeatureSpotlight key={item.id} item={item} onOpenPage={onOpenPage} />
      ))}

      <section className="hs-journey hs-reveal" aria-label="Transport">
        <div className="hs-journey-copy">
          <p className="hs-kicker hs-kicker--blue">The journey</p>
          <h2 className="hs-title hs-title--sm">How the school run looks from your pin</h2>
          <p className="hs-lead">
            Open a school and see bus and train options on the map from the home you just set — then
            walk or drive if that fits better.
          </p>
          <button type="button" className="hs-link" onClick={() => onOpenPage?.("map")}>
            Go to map
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <HomeTransportStage />
      </section>

      <section className="hs-filters hs-reveal" aria-label="Filters">
        <div className="hs-filters-copy">
          <p className="hs-kicker hs-kicker--teal">Filters</p>
          <h2 className="hs-title hs-title--sm">Keep only the schools you actually want</h2>
          <p className="hs-lead">
            Dial in gender, catchment, Ofsted, results, 11+ subjects, and more — the map updates so
            you only see schools that match what matters to your family.
          </p>
          <button type="button" className="hs-link" onClick={() => onOpenPage?.("map")}>
            Try filters on the map
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <HomeFiltersStage />
      </section>

      <section className="hs-depth hs-reveal" aria-label="Deeper tools">
        <div className="hs-band-head">
          <p className="hs-kicker hs-kicker--gold">Then go deeper</p>
          <h2 className="hs-title hs-title--sm">From your home pin to the school file</h2>
          <p className="hs-lead">
            Use the arrows — or swipe — through set home, catchments, offers, Oxbridge, exams, and
            the Year 7 guide.
          </p>
        </div>
        <DepthExplorer onOpenPage={onOpenPage} />
      </section>

      <section className="hs-close hs-reveal">
        <h2 className="hs-close-title">Start with a home</h2>
        <p className="hs-close-text">
          Enter a postcode above, or open the map and place the pin where you live — or where you
          might move.
        </p>
        <button type="button" className="hs-close-cta" onClick={() => onOpenPage?.("map")}>
          Open the map
        </button>
      </section>
    </div>
  );
}
